import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Button,
  Table,
  Select,
  DatePicker,
  message,
  Row,
  Col,
  Typography,
  Tag,
  Space,
  Modal,
  Spin,
  Divider,
} from 'antd';

import {
  DownloadOutlined,
  EyeOutlined,
  FilterOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';

import api from '../../../api/axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const SalesList = () => {
  const [sales, setSales] = useState([]);
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  const [filters, setFilters] = useState({
    shopId: undefined,
    userId: undefined,
    dateRange: null,
  });

  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);

  // ============================================================
  // FETCH SHOPS
  // ============================================================

  const fetchShops = useCallback(async () => {
    try {
      const res = await api.get('/api/shops');

      setShops(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch shops:', err);
    }
  }, []);

  // ============================================================
  // FETCH USERS
  // ============================================================

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/api/users');

      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, []);

  // ============================================================
  // FETCH SALES
  // ============================================================

  const fetchSales = useCallback(async (currentFilters = filters) => {
    setLoading(true);

    try {
      const params = {};

      if (currentFilters.shopId) {
        params.shopId = currentFilters.shopId;
      }

      if (currentFilters.userId) {
        params.userId = currentFilters.userId;
      }

      if (
        currentFilters.dateRange &&
        currentFilters.dateRange.length === 2
      ) {
        params.startDate =
          currentFilters.dateRange[0]
            .startOf('day')
            .toISOString();

        params.endDate =
          currentFilters.dateRange[1]
            .endOf('day')
            .toISOString();
      }

      const res = await api.get(
        '/api/sales/filtered-summaries',
        { params }
      );

      setSales(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch (err) {
      console.error('Failed to fetch sales:', err);

      messageApi.error(
        err?.response?.data?.message ||
          'Failed to fetch sales'
      );
    } finally {
      setLoading(false);
    }
  }, [filters, messageApi]);

  // ============================================================
  // INITIAL DATA LOAD
  // ============================================================

  useEffect(() => {
    fetchShops();
    fetchUsers();
    fetchSales();
  }, [fetchShops, fetchUsers, fetchSales]);

  // ============================================================
  // APPLY FILTER
  // ============================================================

  const handleFilter = () => {
    fetchSales(filters);
  };

  // ============================================================
  // RESET FILTERS
  // ============================================================

  const handleResetFilters = () => {
    const resetFilters = {
      shopId: undefined,
      userId: undefined,
      dateRange: null,
    };

    setFilters(resetFilters);

    fetchSales(resetFilters);
  };

  // ============================================================
  // EXPORT EXCEL
  // ============================================================

  const exportToExcel = () => {
    const data = sales.map((sale) => {
      const customer = sale.customer || {};
      const shop = sale.shop || {};
      const user = sale.user || {};

      return {
        'Sale ID': sale.id,
        Date: sale.createdAt
          ? new Date(sale.createdAt).toLocaleString()
          : '',
        Shop: shop.name || 'N/A',
        Customer: customer.name || 'N/A',
        'Customer Phone': customer.phone || 'N/A',
        'Payment Type': sale.paymentType,
        Subtotal: sale.subtotal || 0,
        Discount: sale.discount || 0,
        VAT: sale.vatAmount || 0,
        Withholding: sale.withholdingAmount || 0,
        'Grand Total': sale.grandTotal || 0,
        Cash: sale.cashAmount || 0,
        Credit: sale.creditAmount || 0,
        Bank1: sale.bank1?.name || 'N/A',
        'Bank1 Amount': sale.bank1Amount || 0,
        Bank2: sale.bank2?.name || 'N/A',
        'Bank2 Amount': sale.bank2Amount || 0,
        User: user.username || 'N/A',
        Remark: sale.remark || '',
        'Items Count': sale.items?.length || 0,
      };
    });

    const worksheet =
      XLSX.utils.json_to_sheet(data);

    worksheet['!cols'] = [
      { wch: 8 },
      { wch: 18 },
      { wch: 18 },
      { wch: 22 },
      { wch: 16 },
      { wch: 14 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 14 },
      { wch: 12 },
      { wch: 12 },
      { wch: 18 },
      { wch: 14 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 20 },
      { wch: 10 },
    ];

    const workbook =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Sales'
    );

    const excelBuffer = XLSX.write(
      workbook,
      {
        bookType: 'xlsx',
        type: 'array',
      }
    );

    const blob = new Blob(
      [excelBuffer],
      {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }
    );

    saveAs(
      blob,
      `Sales_List_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };

  // ============================================================
  // VIEW SALE
  // ============================================================

  const viewSaleItems = (record) => {
    setSelectedSale(record);
    setViewModalVisible(true);
  };

  // ============================================================
  // SALES TABLE
  // ============================================================

  const saleColumns = [
    {
      title: 'Sale ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => (
        <Text strong>#{id}</Text>
      ),
    },

    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) =>
        date
          ? new Date(date).toLocaleString()
          : '—',
    },

    {
      title: 'Shop',
      dataIndex: ['shop', 'name'],
      key: 'shop',
      render: (name) =>
        name || 'N/A',
    },

    {
      title: 'Customer',
      key: 'customer',
      width: 180,
      render: (_, record) =>
        record.customer?.name || 'N/A',
    },

    {
      title: 'Payment',
      dataIndex: 'paymentType',
      key: 'paymentType',
      width: 120,
      render: (type) => (
        <Tag color="blue">
          {type || 'N/A'}
        </Tag>
      ),
    },

    {
      title: 'Grand Total',
      dataIndex: 'grandTotal',
      key: 'grandTotal',
      width: 120,
      align: 'right',
      render: (val) => (
        <Text
          strong
          style={{ color: '#52c41a' }}
        >
          ${(Number(val) || 0).toFixed(2)}
        </Text>
      ),
    },

    {
      title: 'User',
      key: 'user',
      render: (_, record) =>
        record.user?.username || 'N/A',
    },

    {
      title: 'Items',
      key: 'items',
      width: 100,
      render: (_, record) => (
        <Tag color="cyan">
          {record.items?.length || 0}
        </Tag>
      ),
    },

    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() =>
            viewSaleItems(record)
          }
        >
          View
        </Button>
      ),
    },
  ];

  // ============================================================
  // ITEM TABLE
  // ============================================================

  const itemColumns = [
    {
      title: 'Sparepart ID',
      dataIndex: ['sparepart', 'id'],
      key: 'sparepartId',
      width: 100,
    },

    {
      title: 'Part Name',
      dataIndex: ['sparepart', 'partName'],
      key: 'partName',
      render: (val) =>
        val || 'N/A',
    },

    {
      title: 'Part Number',
      dataIndex: ['sparepart', 'partNumber'],
      key: 'partNumber',
      render: (val) =>
        val || 'N/A',
    },

    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'right',
    },

    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 110,
      align: 'right',
      render: (val) =>
        `$${(Number(val) || 0).toFixed(2)}`,
    },

    {
      title: 'Line Total',
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 110,
      align: 'right',
      render: (val) => (
        <Text strong>
          ${(Number(val) || 0).toFixed(2)}
        </Text>
      ),
    },

    {
      title: 'UOM',
      dataIndex: 'measurementUnit',
      key: 'measurementUnit',
      width: 90,
      render: (u) =>
        u || 'pcs',
    },
  ];

  // ============================================================
  // STATISTICS
  // ============================================================

  const totalSalesValue =
    sales.reduce(
      (sum, sale) =>
        sum + (Number(sale.grandTotal) || 0),
      0
    );

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div>
      {contextHolder}

      <Card
        style={{
          borderRadius: 12,
          boxShadow:
            '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        {/* HEADER */}

        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Space>
              <ShoppingCartOutlined
                style={{
                  fontSize: 32,
                  color: '#1890ff',
                }}
              />

              <div>
                <Title
                  level={3}
                  style={{ margin: 0 }}
                >
                  Sales List
                </Title>

                <Text type="secondary">
                  View and filter all sales
                  transactions
                </Text>
              </div>
            </Space>
          </Col>

          <Col>
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              disabled={sales.length === 0}
            >
              Export Excel
            </Button>
          </Col>
        </Row>

        {/* FILTERS */}

        <Card
          size="small"
          style={{
            marginBottom: 24,
            background: '#fafafa',
            border:
              '1px solid #d9d9d9',
          }}
          title={
            <Space>
              <FilterOutlined />
              <Text strong>
                Filters
              </Text>
            </Space>
          }
          extra={
            <Space>
              <Button
                onClick={
                  handleResetFilters
                }
              >
                Reset
              </Button>

              <Button
                type="primary"
                onClick={handleFilter}
                icon={
                  <FilterOutlined />
                }
              >
                Apply
              </Button>
            </Space>
          }
        >
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Text strong>
                Shop:
              </Text>

              <Select
                style={{
                  width: '100%',
                  marginTop: 4,
                }}
                placeholder="All Shops"
                allowClear
                value={filters.shopId}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    shopId: value,
                  }))
                }
              >
                {shops.map((shop) => (
                  <Option
                    key={shop.id}
                    value={shop.id}
                  >
                    {shop.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={8}>
              <Text strong>
                User:
              </Text>

              <Select
                style={{
                  width: '100%',
                  marginTop: 4,
                }}
                placeholder="All Users"
                allowClear
                value={filters.userId}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    userId: value,
                  }))
                }
              >
                {users.map((user) => (
                  <Option
                    key={user.id}
                    value={user.id}
                  >
                    {user.username} (
                    {user.role})
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={8}>
              <Text strong>
                Date Range:
              </Text>

              <RangePicker
                style={{
                  width: '100%',
                  marginTop: 4,
                }}
                value={
                  filters.dateRange
                }
                onChange={(dates) =>
                  setFilters((prev) => ({
                    ...prev,
                    dateRange: dates,
                  }))
                }
              />
            </Col>
          </Row>
        </Card>

        {/* STATS */}

        <Row
          gutter={16}
          style={{ marginBottom: 24 }}
        >
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">
                Total Sales
              </Text>

              <Title
                level={3}
                style={{
                  margin: 0,
                  color: '#1890ff',
                }}
              >
                {sales.length}
              </Title>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">
                Total Value
              </Text>

              <Title
                level={3}
                style={{
                  margin: 0,
                  color: '#52c41a',
                }}
              >
                $
                {totalSalesValue.toFixed(
                  2
                )}
              </Title>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">
                Shops
              </Text>

              <Title
                level={3}
                style={{
                  margin: 0,
                  color: '#faad14',
                }}
              >
                {
                  new Set(
                    sales.map(
                      (s) => s.shopId
                    )
                  ).size
                }
              </Title>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Card>
              <Text type="secondary">
                Users
              </Text>

              <Title
                level={3}
                style={{
                  margin: 0,
                  color: '#722ed1',
                }}
              >
                {
                  new Set(
                    sales.map(
                      (s) => s.userId
                    )
                  ).size
                }
              </Title>
            </Card>
          </Col>
        </Row>

        {/* SALES TABLE */}

        <Card
          size="small"
          style={{
            marginBottom: 16,
            background: '#fafafa',
          }}
          extra={
            <Text type="secondary">
              Showing {sales.length} sales
            </Text>
          }
        >
          <Table
            columns={saleColumns}
            dataSource={sales}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
            }}
            scroll={{
              x: 'max-content',
            }}
            locale={{
              emptyText: loading ? (
                <Spin size="small" />
              ) : (
                <div>
                  <ShoppingCartOutlined
                    style={{
                      fontSize: 32,
                      color: '#d9d9d9',
                    }}
                  />

                  <p
                    style={{
                      marginTop: 8,
                      color: '#999',
                    }}
                  >
                    No sales found
                  </p>
                </div>
              ),
            }}
          />
        </Card>
      </Card>

      {/* SALE DETAILS MODAL */}

      <Modal
        title={
          <Space>
            <EyeOutlined />

            <Text strong>
              Sale Details - #
              {selectedSale?.id}
            </Text>
          </Space>
        }
        open={viewModalVisible}
        onCancel={() =>
          setViewModalVisible(false)
        }
        width={900}
        footer={[
          <Button
            key="close"
            onClick={() =>
              setViewModalVisible(false)
            }
          >
            Close
          </Button>,
        ]}
      >
        {selectedSale && (
          <div>
            <Row
              gutter={16}
              style={{
                marginBottom: 16,
              }}
            >
              <Col span={12}>
                <Text strong>
                  Customer:{' '}
                </Text>

                {selectedSale.customer
                  ?.name || 'N/A'}

                <br />

                <Text type="secondary">
                  Phone:{' '}
                  {selectedSale.customer
                    ?.phone || 'N/A'}
                </Text>
              </Col>

              <Col span={12}>
                <Text strong>
                  Shop:{' '}
                </Text>

                {selectedSale.shop
                  ?.name || 'N/A'}

                <br />

                <Text type="secondary">
                  User:{' '}
                  {selectedSale.user
                    ?.username || 'N/A'}
                </Text>
              </Col>

              <Col
                span={12}
                style={{
                  marginTop: 8,
                }}
              >
                <Text strong>
                  Date:{' '}
                </Text>

                {selectedSale.createdAt
                  ? new Date(
                      selectedSale.createdAt
                    ).toLocaleString()
                  : 'N/A'}
              </Col>

              <Col
                span={12}
                style={{
                  marginTop: 8,
                }}
              >
                <Text strong>
                  Payment:{' '}
                </Text>

                <Tag color="blue">
                  {selectedSale.paymentType ||
                    'N/A'}
                </Tag>
              </Col>
            </Row>

            <Divider>
              Sale Items
            </Divider>

            <Table
              columns={itemColumns}
              dataSource={
                selectedSale.items || []
              }
              rowKey="id"
              pagination={false}
              size="small"
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={4}
                    >
                      <Text strong>
                        Total
                      </Text>
                    </Table.Summary.Cell>

                    <Table.Summary.Cell
                      index={4}
                    />

                    <Table.Summary.Cell
                      index={5}
                    >
                      <Text
                        strong
                        style={{
                          color:
                            '#52c41a',
                        }}
                      >
                        $
                        {(
                          Number(
                            selectedSale.grandTotal
                          ) || 0
                        ).toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>

                    <Table.Summary.Cell
                      index={6}
                      colSpan={2}
                    />
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SalesList;
